import React, { useEffect, useState } from "react";
import usePDA1071_R01Store from "./usePDA1071_R01Store";
import { usePDA1071_R01Content } from "./PDA1071_R01Content";
import { useBarcodeScan } from "src/hooks/useBarcodeScan";

const PDA1071_R01DetailInfo: React.FC = () => {
  const { workOrderDetail, scanValue, setScanValue, scannedItem, selectedItems } =
    usePDA1071_R01Store();
  const { handleScanItem, handleAddItem, handleSaveAll, handleCancel } =
    usePDA1071_R01Content();

  const [moveQty, setMoveQty] = useState<string>("");
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);

  const handleBarcodeScan = async (barcode: string) => {
    const ok = await handleScanItem(barcode.toUpperCase());
    if (ok) {
      setTimeout(() => document.getElementById("moveQty")?.focus(), 100);
    }
  };

  const { scanInputRef, handleScanKeyDown, handleInput } = useBarcodeScan(
    handleBarcodeScan,
    { clearOnScan: false, refocusOnScan: true, debounceMs: 100, autoFocus: true }
  );

  useEffect(() => {
    if (workOrderDetail) scanInputRef.current?.focus();
  }, [workOrderDetail, scanInputRef]);

  useEffect(() => {
    setMoveQty("");
  }, [scannedItem]);

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapTime < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    setLastTapTime(now);
  };

  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const onAdd = () => {
    handleAddItem(Number(moveQty));
    setMoveQty("");
  };

  const handleQtyKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    }
  };

  if (!workOrderDetail) {
    return (
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="text-center text-gray-500 py-8">작업지시를 선택하세요</div>
      </div>
    );
  }

  const header = workOrderDetail.header;

  return (
    <div className="p-4 bg-white border-b border-gray-200">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">지시번호</label>
          <input
            type="text"
            value={header?.productWorkNo ?? ""}
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">Scan</label>
          <div className="flex-1 flex gap-1">
            <input
              ref={scanInputRef}
              type="text"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onInput={handleInput}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (scanValue.trim()) handleScanItem();
                  return;
                }
                handleScanKeyDown(e);
              }}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              placeholder="품목/바코드 스캔 후 Enter"
              autoComplete="off"
              className="flex-1 px-2 max-w-[195px] text-lg border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                keyboardEnabled ? "bg-blue-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">품명</label>
          <input
            type="text"
            value={scannedItem?.itemNm ?? ""}
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">규격</label>
          <input
            type="text"
            value={scannedItem?.itemSpec ?? ""}
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">이동가능</label>
          <input
            type="text"
            value={
              scannedItem?.availableQty != null
                ? `${Number(scannedItem.availableQty).toLocaleString()} ${scannedItem.itemUnit ?? ""}`
                : ""
            }
            disabled
            className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="w-24 text-lg font-medium text-black flex-shrink-0">이동수량</label>
          <div className="flex-1 flex gap-1">
            <input
              id="moveQty"
              type="number"
              value={moveQty}
              onChange={(e) => setMoveQty(e.target.value)}
              onKeyPress={handleQtyKeyPress}
              disabled={!scannedItem}
              placeholder="수량"
              min="0"
              step="0.01"
              className="flex-1 px-2 max-w-[140px] text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <button
              onClick={onAdd}
              disabled={!scannedItem || !moveQty || Number(moveQty) <= 0}
              className="flex-1 bg-blue-500 text-white px-2 rounded text-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
            >
              추가 {selectedItems.length > 0 && `(${selectedItems.length})`}
            </button>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleCancel}
            className="flex-1 py-1 text-lg bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            취소
          </button>
          <button
            onClick={handleSaveAll}
            disabled={selectedItems.length === 0}
            className="flex-1 py-1 text-lg bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {selectedItems.length > 0 ? `이동(${selectedItems.length})` : "이동"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDA1071_R01DetailInfo;
